import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Eye, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format relative time: "2h ago", "5m ago", "just now"
const relTime = (iso) => {
  if (!iso) return '';
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-SG', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export const EmailStatus = ({ docType, docId, compact = false }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/email-tracking/by-doc/${docType}/${docId}`);
      setLogs(res.data || []);
    } catch (e) {
      // Non-critical feature — log for visibility without disrupting UI
      console.warn('EmailStatus: failed to fetch email logs', e?.message || e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (docType && docId) {
      fetchLogs();
      // Poll every 30s while page is open so opens show up live
      const t = setInterval(fetchLogs, 30000);
      return () => clearInterval(t);
    }
  }, [docType, docId]);

  if (loading || logs.length === 0) return null;

  const latest = logs[0];
  const isOpened = !!latest.opened_at;
  const isBounced = !!latest.bounced_at;
  const isDelivered = !!latest.delivered_at;

  // Status badge
  let icon, color, label, sub;
  if (isBounced) {
    icon = <AlertTriangle size={14} />;
    color = 'bg-red-50 border-red-200 text-red-700';
    label = 'Bounced';
    sub = `to ${latest.recipient} · ${relTime(latest.sent_at)}`;
  } else if (isOpened) {
    icon = <Eye size={14} />;
    color = 'bg-green-50 border-green-200 text-green-700';
    label = `Opened${latest.open_count > 1 ? ` (${latest.open_count}×)` : ''}`;
    sub = `${relTime(latest.opened_at)} · sent to ${latest.recipient}`;
  } else if (isDelivered) {
    icon = <CheckCircle2 size={14} />;
    color = 'bg-blue-50 border-blue-200 text-blue-700';
    label = 'Delivered';
    sub = `to ${latest.recipient} · ${relTime(latest.sent_at)}`;
  } else {
    icon = <Clock size={14} />;
    color = 'bg-gray-50 border-gray-200 text-gray-600';
    label = 'Sent';
    sub = `to ${latest.recipient} · ${relTime(latest.sent_at)}`;
  }

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${color}`} data-testid="email-status-compact" title={`Last sent ${fmtDate(latest.sent_at)} to ${latest.recipient}`}>
        {icon} {label}
      </span>
    );
  }

  return (
    <div className="mt-3" data-testid="email-status">
      <button
        onClick={() => setShowHistory(!showHistory)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm border transition-colors hover:opacity-80 ${color}`}
        data-testid="email-status-toggle"
      >
        <Mail size={14} />
        <span className="font-semibold">{label}</span>
        <span className="text-xs opacity-75">— {sub}</span>
        <span className="text-xs underline ml-1">{showHistory ? 'Hide history' : `${logs.length} send${logs.length > 1 ? 's' : ''}`}</span>
      </button>

      {showHistory && (
        <div className="mt-2 border rounded-md bg-white shadow-sm" data-testid="email-history">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Sent</th>
                <th className="text-left px-3 py-2 font-medium">Recipient</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Opened</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((row) => (
                <tr key={row.resend_email_id} className="border-b last:border-0">
                  <td className="px-3 py-2">{fmtDate(row.sent_at)}</td>
                  <td className="px-3 py-2 text-gray-700">{row.recipient}</td>
                  <td className="px-3 py-2">
                    {row.bounced_at ? (
                      <span className="text-red-600">Bounced</span>
                    ) : row.opened_at ? (
                      <span className="text-green-600">Delivered + Opened</span>
                    ) : row.delivered_at ? (
                      <span className="text-blue-600">Delivered</span>
                    ) : (
                      <span className="text-gray-500">Sent</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {row.first_opened_at ? `${fmtDate(row.first_opened_at)}${row.open_count > 1 ? ` (${row.open_count}×)` : ''}` : <span className="text-gray-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
